import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PaymentElement } from 'react-stripe-js';
import { View } from 'foundations-components/components/View';
import { useNavMarker } from 'react-rhumb';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import usePageInteractionTracking from '../../hooks/usePageInteractionTracking';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { AUTO, NEVER } from '../../constants/PaymentElement';
import { stripeToHSPaymentMethod } from '../../utils/paymentMethods';
import { getBillingFieldSetting, getBillingNameSetting, getPaymentElementLayout } from '../../utils/paymentElementOptions';
import { setErrorMessages, clearCardErrors, clearBankDebitErrors } from '../../state/errorMessagesSlice';
import { trackStripePaymentElementLoadError } from '../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
export const PaymentElementSection = ({
  resetSubmitError
}) => {
  const [ready, setReady] = useState(false);
  useNavMarker('PAYMENT_ELEMENT_LOADED', ready);
  const {
    checkoutSession: checkoutSessionMap,
    formState,
    dispatch
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const checkoutSession = useMemo(() => checkoutSessionMap.toJS(), [checkoutSessionMap]);
  const paymentMethod = formState.get('paymentMethod');
  const stripePaymentMethod = formState.get('stripePaymentMethod');
  const setStripePaymentMethod = method => {
    dispatch({
      type: 'setStripePaymentMethod',
      data: {
        value: method
      }
    });
  };
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const collectShippingAddress = checkoutSession.shippableCountries && !!checkoutSession.shippableCountries.length;
  const collectFullBillingAddress = checkoutSession.collectFullBillingAddress;
  const onPaymentElementChange = changeData => {
    const newPaymentMethod = changeData.value.type;
    setStripePaymentMethod(newPaymentMethod);
    resetSubmitError();
    if (changeData.complete) {
      dispatch({
        type: 'resetStripeValidationError'
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: false
        }
      }));
    } else {
      dispatch({
        type: 'setStripeValidationError'
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: true
        }
      }));
    }
    if (stripePaymentMethod !== newPaymentMethod) {
      trackFirstFormFieldInteraction({
        section: INTERACTION_SECTIONS.paymentInfo,
        target: INTERACTION_TARGETS.paymentMethod
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: false
        }
      }));

      // don't show card related errors if switching to bank debit payment method
      if (newPaymentMethod !== 'card') {
        reduxDispatch(clearCardErrors());
      } else {
        reduxDispatch(clearBankDebitErrors());
      }
      dispatch({
        type: 'setPaymentMethod',
        data: {
          value: stripeToHSPaymentMethod(newPaymentMethod)
        }
      });
    }
  };
  const billingFieldSetting = getBillingFieldSetting(collectFullBillingAddress, collectShippingAddress, paymentMethod, checkoutSession.isAutomatedSalesTaxEnabled);
  const paymentElementOptions = useMemo(() => ({
    fields: {
      billingDetails: {
        name: getBillingNameSetting(paymentMethod),
        email: NEVER,
        phone: AUTO,
        address: {
          line1: AUTO,
          line2: AUTO,
          city: AUTO,
          state: AUTO,
          country: billingFieldSetting,
          postalCode: billingFieldSetting
        }
      }
    },
    wallets: {
      applePay: AUTO,
      googlePay: AUTO
    },
    layout: getPaymentElementLayout(checkoutSession.paymentMethods),
    terms: {
      card: NEVER,
      usBankAccount: NEVER,
      sepaDebit: NEVER,
      applePay: NEVER,
      googlePay: NEVER,
      klarna: NEVER,
      affirm: NEVER
    }
  }), [paymentMethod, billingFieldSetting, checkoutSession.paymentMethods]);
  const handleLoadError = ({
    error
  }) => {
    trackStripePaymentElementLoadError({
      stripeErrorMessage: error === null || error === void 0 ? void 0 : error.message,
      stripeErrorCode: error === null || error === void 0 ? void 0 : error.code
    });
  };
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "preloaded-stripe-element-container",
    "data-ready": ready,
    marginBlockStart: "400",
    children: /*#__PURE__*/_jsx(PaymentElement, {
      id: "payment-info-input-container",
      onChange: onPaymentElementChange,
      onReady: () => setReady(true),
      options: paymentElementOptions,
      onLoadError: handleLoadError
    })
  });
};
export default PaymentElementSection;